import { useContext, useEffect } from 'react';
import { ServicesContext } from 'app/items/shared/context_interface';
import {
    shapeEditorMachine,
    ShapeEditorActorContext,
} from 'app/agriculture/forms/components/ShapeEditor/ShapeEditorMachine';
import { LineStringGeometry } from 'app/agriculture/queries/types/geometry/LineStringGeometry';
import { PolygonGeometry } from 'app/agriculture/queries/types/geometry/PolygonGeometry';
import {
    geometryToCartesian3Array,
    cartesian3ToGeometryCoordinates,
} from 'app/agriculture/forms/components/ShapeEditor/utils';
import { ShapeEditorRenderer } from 'app/agriculture/forms/components/ShapeEditor/ShapeEditorRenderer';
import { ShapeEditorControl } from 'app/agriculture/forms/components/ShapeEditor/ShapeEditorControl/ShapeEditorControl';
import { enableScreenPan } from 'app/agriculture/hooks/useCesium/cesiumUtils';
import { ShapeEditorInputHandlers } from 'app/agriculture/forms/components/ShapeEditor/ShapeEditorInputHandlers';
import { InfraType } from 'app/agriculture/mutations/types/entityConfig';

interface ShapeEditorProps<T extends LineStringGeometry | PolygonGeometry> {
    geometry: T;
    onChange: (geometry: T) => void;
    infraType: InfraType;
}

/*The editor that allows the user to add and edit polygons - like paddocks
 * and polylines - like waterways*/
export function ShapeEditor<T extends LineStringGeometry | PolygonGeometry>({
    geometry,
    onChange,
    infraType,
}: ShapeEditorProps<T>) {
    const { mapInputHandlerService, mapService } = useContext(ServicesContext);
    const initialPoints = geometryToCartesian3Array(geometry);

    function disableOldEventHandler() {
        void mapInputHandlerService.setActiveHandlerByName('noopHandler');
    }
    function enableOldEventHandler() {
        void mapInputHandlerService.setDefaultHandler();
    }
    const machine = shapeEditorMachine.provide({
        actions: {
            triggerChange: ({ context }) => {
                const { points, mode } = context;
                const coordinates = cartesian3ToGeometryCoordinates(points, mode);
                onChange({
                    ...geometry,
                    coordinates,
                });
            },
        },
    });

    useEffect(() => {
        disableOldEventHandler();
        return () => {
            enableOldEventHandler();
            enableScreenPan(mapService.viewer);
        };
    }, []);

    return (
        <ShapeEditorActorContext.Provider
            logic={machine}
            options={{
                input: {
                    points: initialPoints,
                    mode: geometry.type,
                    isSnappingEnabled: true,
                },
            }}
        >
            <ShapeEditorRenderer />
            <ShapeEditorInputHandlers />
            <ShapeEditorControl infraType={infraType} />
        </ShapeEditorActorContext.Provider>
    );
}
